import React, { useState } from 'react';
import { useAuthStore } from '../../store/authStore';

type AuthMode = 'login' | 'register';

const AuthModal: React.FC = () => {
    const [mode, setMode] = useState<AuthMode>('login');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [name, setName] = useState('');
    const [validationError, setValidationError] = useState('');

    const { login, register, isLoading, error, clearError, isAuthModalOpen, closeAuthModal } = useAuthStore();

    const resetForm = () => {
        setEmail('');
        setPassword('');
        setConfirmPassword('');
        setName('');
        setValidationError('');
        clearError();
    };

    const switchMode = (newMode: AuthMode) => {
        resetForm();
        setMode(newMode);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setValidationError('');

        // Validation
        if (!email || !password) {
            setValidationError('Vui lòng nhập email và mật khẩu');
            return;
        }

        if (mode === 'register') {
            if (password.length < 8) {
                setValidationError('Mật khẩu phải có ít nhất 8 ký tự');
                return;
            }
            if (password !== confirmPassword) {
                setValidationError('Mật khẩu xác nhận không khớp');
                return;
            }
        }

        let success = false;
        if (mode === 'login') {
            success = await login({ email, password });
        } else {
            success = await register({ email, password, name: name || undefined });
        }

        if (success) {
            resetForm();
            closeAuthModal();
        }
    };

    const handleClose = () => {
        resetForm();
        closeAuthModal();
    };

    if (!isAuthModalOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                onClick={handleClose}
            />

            {/* Modal */}
            <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
                {/* Header */}
                <div className="bg-gradient-to-r from-red-600 to-red-500 px-6 py-4">
                    <h2 className="text-xl font-bold text-white">
                        {mode === 'login' ? 'Đăng Nhập' : 'Tạo Tài Khoản'}
                    </h2>
                    <p className="text-red-100 text-sm mt-1">
                        {mode === 'login'
                            ? 'Đăng nhập để đồng bộ ghi chú'
                            : 'Tạo tài khoản để lưu trữ ghi chú'}
                    </p>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                    {/* Name (register only) */}
                    {mode === 'register' && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Tên (tùy chọn)
                            </label>
                            <input
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                                placeholder="Nhập tên của bạn"
                            />
                        </div>
                    )}

                    {/* Email */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Email
                        </label>
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                            placeholder="email@example.com"
                            required
                        />
                    </div>

                    {/* Password */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Mật khẩu
                        </label>
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                            placeholder={mode === 'register' ? 'Tối thiểu 8 ký tự' : '••••••••'}
                            required
                        />
                    </div>

                    {/* Confirm Password (register only) */}
                    {mode === 'register' && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Xác nhận mật khẩu
                            </label>
                            <input
                                type="password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                                placeholder="Nhập lại mật khẩu"
                                required
                            />
                        </div>
                    )}

                    {/* Errors */}
                    {(validationError || error) && (
                        <div className="p-3 bg-red-50 border border-red-200 rounded-xl">
                            <p className="text-red-600 text-sm">
                                {validationError || error}
                            </p>
                        </div>
                    )}

                    {/* Submit Button */}
                    <button
                        type="submit"
                        disabled={isLoading}
                        className="w-full py-3 px-4 bg-gradient-to-r from-red-600 to-red-500 text-white font-semibold rounded-xl
                            hover:from-red-700 hover:to-red-600 focus:ring-2 focus:ring-red-500 focus:ring-offset-2
                            disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                        {isLoading ? (
                            <span className="flex items-center justify-center gap-2">
                                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                </svg>
                                Đang xử lý...
                            </span>
                        ) : mode === 'login' ? 'Đăng Nhập' : 'Tạo Tài Khoản'}
                    </button>

                    {/* Divider */}
                    <div className="relative my-4">
                        <div className="absolute inset-0 flex items-center">
                            <div className="w-full border-t border-gray-200"></div>
                        </div>
                        <div className="relative flex justify-center text-sm">
                            <span className="px-3 bg-white text-gray-500">hoặc</span>
                        </div>
                    </div>

                    {/* Google Sign-in Button */}
                    <button
                        type="button"
                        onClick={() => {
                            const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8080';
                            window.location.href = `${apiUrl}/api/v1/auth/google`;
                        }}
                        className="w-full py-3 px-4 bg-white border-2 border-gray-200 text-gray-700 font-semibold rounded-xl
                            hover:bg-gray-50 hover:border-gray-300 focus:ring-2 focus:ring-gray-200 focus:ring-offset-2
                            transition-all flex items-center justify-center gap-3"
                    >
                        {/* Google Icon */}
                        <svg className="w-5 h-5" viewBox="0 0 24 24">
                            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                        </svg>
                        Đăng nhập với Google
                    </button>
                </form>

                {/* Footer - Switch mode */}
                <div className="px-6 pb-6 pt-2 border-t border-gray-100">
                    <p className="text-center text-gray-600">
                        {mode === 'login' ? (
                            <>
                                Chưa có tài khoản?{' '}
                                <button
                                    type="button"
                                    onClick={() => switchMode('register')}
                                    className="text-red-600 font-semibold hover:underline"
                                >
                                    Đăng ký ngay
                                </button>
                            </>
                        ) : (
                            <>
                                Đã có tài khoản?{' '}
                                <button
                                    type="button"
                                    onClick={() => switchMode('login')}
                                    className="text-red-600 font-semibold hover:underline"
                                >
                                    Đăng nhập
                                </button>
                            </>
                        )}
                    </p>
                </div>

                {/* Close button */}
                <button
                    type="button"
                    onClick={handleClose}
                    className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors"
                    aria-label="Đóng"
                >
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>
        </div>
    );
};

export default AuthModal;
